import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import CheckIcon from '@mui/icons-material/Check';
import { alpha, Stack, Typography, useTheme } from '@mui/material';
import { LinkedinTypes, ProductGroups } from 'enums';
import { RecruiterLocationSearch, RegularConstraint } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RotatingLines } from 'react-loader-spinner';
import { makeStyles } from 'tss-react/mui';
import { LinkedinSearch, LinkedinSearchResult, SelectorIds } from '../../../../campaign-builder.types';
import FoundLinkedinSearch from './found-linkedin-search';
import Location from './location';
import RecruiterLocation from './recruiter-location';

interface Props {
  productGroup: ProductGroups;
  linkedinProduct: LinkedinTypes | ''; // the user's chosen product (RECRUITER, LINKEDIN, or SALES_NAV)
  paiSearchLanguage: string[];
  includeOwnContacts: boolean;

  regularLocationConstraints: RegularConstraint;
  recruiterLocationConstraints: RecruiterLocationSearch;

  linkedinAccounts: LinkedinAccount[];
  linkedinAccount: LinkedinAccount | undefined;
  separateLinkedinAccount: string;

  linkedinSearch: LinkedinSearch;

  handleLinkedinProductChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handlePaiSearchLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleIncludeOwnContactsChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleLocationFieldChange(event: React.ChangeEvent<HTMLInputElement>): void;
  searchKeyUp(): void;
  searchKeyDown(): void;
  handleMultiSelectLocationsChange(value: string): () => void;
  handleMultiSelectLocationsRemove(value: string): () => void;
  handleSelectRecruiterLocationConstraint(searchType: 'geo' | 'zip'): (value: string) => () => void;
  handleRemoveRecruiterLocationConstraint(searchType: 'geo' | 'zip', urn: string): () => void;
  handleRecruiterLocationConstraintQueryChange(searchType: 'geo' | 'zip'): (event: React.ChangeEvent<HTMLInputElement>) => void;
  searchRecruiterLocationKeyUp(searchType: 'geo' | 'zip'): () => void;
  searchRecruiterLocationKeyDown(): void;
  handleRecruiterLocationAreaChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleResetLinkedinSearch(): void;
  handleResetLocationSettings(): void;
  handleSeparateLinkedinAccountChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleStartLinkedinSearch(): void;
  handleOpenRefineLinkedinSearchDialog(linkedinSearch: LinkedinSearchResult, idx: number): () => void;
  handleOpenCreateNewSearchDialog(): void;
  onOpenFeedbackDialog?(): void;
}

const PaiContactSearch: React.FC<Props> = ({
  productGroup,
  linkedinProduct,
  paiSearchLanguage,
  includeOwnContacts,
  regularLocationConstraints,
  recruiterLocationConstraints,
  linkedinAccounts,
  linkedinAccount,
  separateLinkedinAccount,
  linkedinSearch,
  handleLinkedinProductChange,
  handlePaiSearchLanguageChange,
  handleIncludeOwnContactsChange,
  handleLocationFieldChange,
  searchKeyUp,
  searchKeyDown,
  handleMultiSelectLocationsChange,
  handleMultiSelectLocationsRemove,
  handleSelectRecruiterLocationConstraint,
  handleRemoveRecruiterLocationConstraint,
  handleRecruiterLocationConstraintQueryChange,
  searchRecruiterLocationKeyUp,
  searchRecruiterLocationKeyDown,
  handleRecruiterLocationAreaChange,
  handleResetLinkedinSearch,
  handleResetLocationSettings,
  handleSeparateLinkedinAccountChange,
  handleStartLinkedinSearch,
  handleOpenRefineLinkedinSearchDialog,
  onOpenFeedbackDialog,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  // Helper function to handle product-specific translations
  const getLocalizedText = (key: string): string => {
    if (productGroup === ProductGroups.LEAD_BOOSTER) {
      const leadBoosterKey = `${key}_LeadBooster`;
      return t(leadBoosterKey) || t(key);
    }
    // Default is for Active Sourcing
    const activeSourcingKey = `${key}_ActiveSourcing`;
    return t(activeSourcingKey) || t(key);
  };

  const languageOptions = [
    { label: getLocalizedText('aiBriefingLanguage'), value: 'default' },
    { label: getLocalizedText('english'), value: 'en' },
  ];

  const isLinkedinProductSelected = !!linkedinProduct;

  const areLocationsSelected =
    (!_.isEmpty(regularLocationConstraints.selected) && linkedinProduct === LinkedinTypes.LINKEDIN) ||
    ((!_.isEmpty(recruiterLocationConstraints['geo'].selected) || !_.isEmpty(recruiterLocationConstraints['zip'].selected)) &&
      (linkedinProduct === LinkedinTypes.RECRUITER || linkedinProduct === LinkedinTypes.SALES_NAV));

  const areSearchesFound = linkedinSearch.searchResults && !_.isEmpty(linkedinSearch.searchResults);

  const isLinkedinProductDisabled = Boolean(areLocationsSelected || areSearchesFound);

  // Account, product and language come pre-filled; they only open on request.
  const [showSettings, setShowSettings] = useState(false);

  const scrollToSection = (section: SelectorIds) => {
    return () => {
      const scrollContainer = document.getElementById(SelectorIds.STEP_CONTENT);
      if (!_.isNil(scrollContainer)) {
        const targetSection = document.getElementById(section);
        if (!_.isNil(targetSection)) {
          const scrollOffset = targetSection.offsetTop - scrollContainer.offsetTop;
          scrollContainer.scrollTo({
            top: scrollOffset,
            behavior: 'smooth',
          });
        }
      }
    };
  };

  useEffect(() => {
    if (areSearchesFound) {
      scrollToSection(SelectorIds.PAI_SEARCH_SAVE_SEARCH)();
    }
  }, [areSearchesFound]);

  const loadingSearch = linkedinSearch.loading;

  // Determine which LinkedIn account to use
  const selectedLinkedinAccount = separateLinkedinAccount ? linkedinAccounts.find((acc) => acc._id === separateLinkedinAccount) : linkedinAccount;

  // Get the product type from the selected account or default to LINKEDIN
  const liProductType = selectedLinkedinAccount?.liProductType ?? LinkedinTypes.LINKEDIN;

  // Define available products based on the account type
  const showLinkedin = true; // LinkedIn is always available
  const showSalesNav = liProductType === LinkedinTypes.SALES_NAV;
  const showRecruiter = liProductType === LinkedinTypes.RECRUITER;

  const productLabel =
    linkedinProduct === LinkedinTypes.RECRUITER ? 'LinkedIn Recruiter' : linkedinProduct === LinkedinTypes.SALES_NAV ? 'Sales Navigator' : 'LinkedIn';
  const languageLabel = languageOptions
    .filter((langOpt) => paiSearchLanguage.includes(langOpt.value))
    .map((langOpt) => langOpt.label)
    .join(', ');
  const settingsSummary = [productLabel, selectedLinkedinAccount?.userEmail, languageLabel ? `${t('kiSearchLanguages')}: ${languageLabel}` : null]
    .filter(Boolean)
    .join(' · ');

  const canStart = areLocationsSelected && !loadingSearch && !_.isEmpty(paiSearchLanguage);

  const afterSteps = ['kiSearchAfter1', 'kiSearchAfter2', 'kiSearchAfter3', 'kiSearchAfter4'];

  return (
    <>
      {linkedinSearch.loading ? (
        <Stack className={classes.loader} alignItems={'center'} justifyContent={'center'} gap={3}>
          <RotatingLines strokeWidth={'3'} width={'100'} strokeColor={theme.palette.primary.main} />
          <div>
            <Typography className={classes.loadingTextTitle} align={'center'} mb={'8px'}>
              {getLocalizedText('sourcingPotentialTalentOnLinkedIn')}
            </Typography>
            <Typography className={classes.loadingText} align={'center'}>
              {linkedinSearch.statusMessage || getLocalizedText('identifyingQualifiedTalentSearches')}
            </Typography>
          </div>
        </Stack>
      ) : null}

      <section className={classes.headerSection}>
        <Typography className={classes.blueTitle}>{t('kiSearchEyebrow')}</Typography>
        <Typography component="h2" className={classes.title}>
          {getLocalizedText('kiSearchHeadline')}
        </Typography>
        <Typography className={classes.greyText}>{getLocalizedText('kiSearchIntro')}</Typography>
      </section>

      <section id={SelectorIds.SEARCH_LOCATION} className={classes.section}>
        <ArrowRightAltIcon className={cx(classes.arrow, !areLocationsSelected ? 'active' : undefined)} />
        <Typography className={classes.subTitle} mb={'8px'}>
          {getLocalizedText('typeInYourSearchLocation')}
        </Typography>
        <Typography className={classes.greyText} mb={'16px'}>
          {getLocalizedText('selectLocationForContactSearch')}
        </Typography>

        {!isLinkedinProductSelected ? null : linkedinProduct === LinkedinTypes.RECRUITER || linkedinProduct === LinkedinTypes.SALES_NAV ? (
          <RecruiterLocation
            recruiterLocationConstraints={recruiterLocationConstraints}
            handleSelectRecruiterLocationConstraint={handleSelectRecruiterLocationConstraint}
            handleRemoveRecruiterLocationConstraint={handleRemoveRecruiterLocationConstraint}
            handleRecruiterLocationConstraintQueryChange={handleRecruiterLocationConstraintQueryChange}
            searchRecruiterLocationKeyUp={searchRecruiterLocationKeyUp}
            searchRecruiterLocationKeyDown={searchRecruiterLocationKeyDown}
            handleRecruiterLocationAreaChange={handleRecruiterLocationAreaChange}
            handleResetLinkedinSearch={handleResetLinkedinSearch}
            handleResetLocationSettings={handleResetLocationSettings}
          />
        ) : (
          <Location
            regularLocationConstraints={regularLocationConstraints}
            handleLocationFieldChange={handleLocationFieldChange}
            searchKeyUp={searchKeyUp}
            searchKeyDown={searchKeyDown}
            handleMultiSelectLocationsChange={handleMultiSelectLocationsChange}
            handleMultiSelectLocationsRemove={handleMultiSelectLocationsRemove}
          />
        )}
      </section>

      <section id={SelectorIds.PAI_SEARCH_START_SEARCH} className={classes.section}>
        <ArrowRightAltIcon className={cx(classes.arrow, areLocationsSelected && !areSearchesFound ? 'active' : undefined)} />
        <Typography className={classes.subTitle} mb={'12px'}>
          {t('kiSearchAfterTitle')}
        </Typography>
        <Stack component="ul" gap={1} className={classes.plainList} mb={'24px'}>
          {afterSteps.map((key) => (
            <li key={key} className={classes.afterItem}>
              <CheckIcon className={classes.afterIcon} aria-hidden="true" />
              <Typography className={classes.greyText}>{getLocalizedText(key)}</Typography>
            </li>
          ))}
        </Stack>

        <Stack direction={'row'} alignItems={'center'} gap={2} flexWrap={'wrap'}>
          <PaiButton
            text={getLocalizedText('kiSearchStart')}
            border={'rounded'}
            color={'primary'}
            size={'medium'}
            action={handleStartLinkedinSearch}
            loading={loadingSearch}
            disabled={!canStart}
          />
          {!areLocationsSelected ? <Typography className={classes.greyText}>{t('kiSearchLocationMissing')}</Typography> : null}
        </Stack>

        {linkedinSearch.blockedMessage ? (
          <Typography className={classes.errorText} role="alert" mt={'12px'}>
            {linkedinSearch.blockedMessage}
          </Typography>
        ) : null}
        {linkedinSearch.isEmpty ? (
          <Stack spacing={1} sx={{ marginTop: '16px' }}>
            <Typography className={classes.highlightText}>
              {linkedinSearch.feedback?.message || getLocalizedText('noContactSearchesFound')}
            </Typography>
            {linkedinSearch.feedback ? (
              <PaiButton
                text={getLocalizedText('talentSearchViewSuggestions')}
                action={onOpenFeedbackDialog}
                size="extra-small"
                border="rounded"
                color="secondary"
                style={{ alignSelf: 'flex-start', marginTop: 8 }}
              />
            ) : null}
          </Stack>
        ) : null}

        <Stack direction={'row'} alignItems={'center'} gap={1} flexWrap={'wrap'} mt={'20px'}>
          <Typography className={classes.smallGreyText}>
            {t('kiSearchSearchesWith')} {settingsSummary}
          </Typography>
          <PaiLink
            text={showSettings ? t('kiSearchHideSettings') : t('kiSearchChangeSettings')}
            variant={'primary'}
            size={'sm'}
            bold
            action={() => setShowSettings((open) => !open)}
          />
        </Stack>

        {showSettings ? (
          <Stack className={classes.settings} gap={3}>
            <div id={SelectorIds.CRAWLING_ACCOUNT}>
              <Typography className={classes.subTitle} mb={'8px'}>
                {getLocalizedText('selectLinkedInAccountForSourcing')}
              </Typography>
              <Typography className={classes.greyText} mb={'16px'}>
                {getLocalizedText('useDifferentLinkedInAccountsForSourcing')}
              </Typography>
              <PaiSelect value={separateLinkedinAccount} onChange={handleSeparateLinkedinAccountChange} style={{ maxWidth: '350px' }}>
                <PaiMenuItem value={''}>{getLocalizedText('none')}</PaiMenuItem>
                {linkedinAccounts.map((account) => (
                  <PaiMenuItem key={account._id} value={account._id}>
                    {account.userEmail}
                  </PaiMenuItem>
                ))}
              </PaiSelect>
            </div>

            <div id={SelectorIds.LINKEDIN_PRODUCT}>
              <Typography className={classes.subTitle} mb={'16px'}>
                {getLocalizedText('whichLinkedInProductForSourcing')}
              </Typography>
              <Stack direction={'row'} gap={3} flexWrap={'wrap'}>
                {showLinkedin && (
                  <PaiCheckbox
                    label={'LinkedIn'}
                    checked={linkedinProduct === LinkedinTypes.LINKEDIN}
                    value={LinkedinTypes.LINKEDIN}
                    onChange={handleLinkedinProductChange}
                    disabled={isLinkedinProductDisabled}
                  />
                )}
                {showSalesNav && (
                  <PaiCheckbox
                    label={'Sales Navigator'}
                    checked={linkedinProduct === LinkedinTypes.SALES_NAV}
                    value={LinkedinTypes.SALES_NAV}
                    onChange={handleLinkedinProductChange}
                    disabled={isLinkedinProductDisabled}
                  />
                )}
                {showRecruiter && (
                  <PaiCheckbox
                    label={'Recruiter'}
                    checked={linkedinProduct === LinkedinTypes.RECRUITER}
                    value={LinkedinTypes.RECRUITER}
                    onChange={handleLinkedinProductChange}
                    disabled={isLinkedinProductDisabled}
                  />
                )}
              </Stack>
            </div>

            <div id={SelectorIds.PAI_SEARCH_LANGUAGE}>
              <Typography className={classes.subTitle} mb={'8px'}>
                {getLocalizedText('selectLanguagesForSearch')}
              </Typography>
              <Typography className={classes.greyText} mb={'16px'}>
                {getLocalizedText('chooseLanguagesForSearch')}
              </Typography>
              <Stack direction={'row'} alignItems={'center'} gap={4} flexWrap={'wrap'}>
                {languageOptions.map((langOpt) => (
                  <PaiCheckbox
                    key={langOpt.value}
                    label={langOpt.label}
                    value={langOpt.value}
                    checked={paiSearchLanguage.includes(langOpt.value)}
                    onChange={handlePaiSearchLanguageChange}
                  />
                ))}
              </Stack>
            </div>

            <div id={SelectorIds.PAI_SEARCH_NETWORK}>
              <Typography className={classes.subTitle} mb={'8px'}>
                {getLocalizedText('includeOwnContacts')}
              </Typography>
              <Typography className={classes.greyText} mb={'16px'}>
                {getLocalizedText('includeOwnContactsDescription')}
              </Typography>
              <PaiCheckbox
                label={getLocalizedText('includeOwnContactsLabel')}
                checked={includeOwnContacts}
                onChange={handleIncludeOwnContactsChange}
              />
            </div>
          </Stack>
        ) : null}
      </section>

      {areSearchesFound ? (
        <section id={SelectorIds.PAI_SEARCH_SAVE_SEARCH} className={classes.section}>
          <ArrowRightAltIcon className={cx(classes.arrow, 'active')} />
          <Typography className={classes.subTitle} mb={'8px'}>
            {getLocalizedText('addLinkedinSearchesToLeadBank')}
          </Typography>
          <Typography className={classes.greyText} mb={'16px'}>
            {getLocalizedText('reviewSearchSamplesForLeads')}
          </Typography>
          <FoundLinkedinSearch
            linkedinSearch={linkedinSearch}
            productGroup={productGroup}
            searchResults={linkedinSearch.searchResults ?? []}
            handleOpenRefineLinkedinSearchDialog={handleOpenRefineLinkedinSearchDialog}
          />
        </section>
      ) : null}
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: alpha('#FFF', 0.9),
    zIndex: 99,
  },
  loadingTextTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  loadingText: {
    fontSize: theme.typography.pxToRem(16),
    maxWidth: '450px',
  },
  headerSection: {
    position: 'relative',
    padding: '24px 0 24px 48px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  section: {
    position: 'relative',
    padding: '24px 48px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  arrow: {
    display: 'none',
    position: 'absolute',
    top: '18px',
    left: '0px',
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.primary.main,
    '&.active': {
      display: 'inline-block',
    },
  },
  blueTitle: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  title: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  smallGreyText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    overflowWrap: 'anywhere',
  },
  plainList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  afterItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  afterIcon: {
    fontSize: theme.typography.pxToRem(18),
    color: theme.palette.primary.main,
    marginTop: '2px',
  },
  settings: {
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: `1px solid ${theme.palette.greys.lighter}`,
  },
  highlightText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
  },
  errorText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.error.main,
  },
}));

export default PaiContactSearch;
